import { apiJson } from '../api';
import type { StoryElement } from '../types';
import type { SavedSearchRunState } from '../utils/savedSearchRunState';
import type { TimingCategory } from '../utils/searchTiming';

export interface SavedSearch {
  id: string;
  user_id: string;
  name: string;
  description: string;
  description_hash: string;
  relevance_constant: number;
  freshness_constant: number;
  sort_order: number;
  is_tldr_preview?: boolean;
  feed_enabled: boolean;
  /** Whether this feed is merged into its owner's For You page. */
  in_for_you: boolean;
  feed_last_scheduled_at?: string | null;
  run_count?: number;
  latest_search_id?: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SavedSearchPayload {
  name: string;
  description: string;
  relevance_constant: number;
  freshness_constant: number;
  is_tldr_preview?: boolean;
  feed_enabled?: boolean;
  in_for_you?: boolean;
}

export interface RecentSearch {
  search_id: string;
  description_id: string;
  description: string;
  title?: string | null;
  created_at: string;
  status: string;
}

export interface SavedSearchRunPayload {
  description?: string;
  description_id?: string;
  relevance_constant?: number;
  freshness_constant?: number;
  limit?: number;
  candidate_limit?: number;
  exclude_categories?: string[];
  reading_minutes_min?: number;
  reading_minutes_max?: number;
  /** Skip the server's run-reuse policy: always start a full live run. */
  force?: boolean;
  /** Which judge model to run. Omit for the default. */
  model?: string;
  /** Candidate lookback window. Defaults to 24 hours. */
  fast_search_window_hours?: 24 | 72;
  /** Expand the brief into sub-queries and lexical vocabulary before
   *  retrieval. Admin-only in the composer, and still subject to the
   *  deployment kill switch, so requesting it is a no-op where it is off. */
  expand_query?: boolean;
}

/** One option in the search model picker (served by the backend so the
 *  allowlist, its prices, and its caveats live in exactly one place). */
export interface SearchModel {
  id: string;
  label: string;
  prompt_usd_per_m: number;
  completion_usd_per_m: number;
  note: string;
  is_default: boolean;
}

/** Which cover of the judge pair speaks for it when both agree relevant. */
export type JudgeCover = 'primary' | 'hedge';

/** The two judges a fast search runs, as configured and as a run reports them. */
export interface SearchJudgeCovers {
  primary: Omit<SearchModel, 'is_default'>;
  hedge: Omit<SearchModel, 'is_default'>;
  authoritative: JudgeCover;
  authoritative_model_id: string;
  provider: string;
  lane: string;
  deadline_ms: number;
}

/** The judge configuration the admin page edits: the live pair, plus the
 *  allowlist both covers are chosen from. */
export interface SearchJudgeConfig extends SearchJudgeCovers {
  primary_model_id: string;
  hedge_model_id: string;
  models: SearchModel[];
}

export interface SearchPrompt {
  id: string;
  prompt_type: string;
  system_prompt: string;
  user_prompt_template: string;
  model: string;
}

/** A stored fast-search prompt row, as listed on the admin page. Saving from
 *  that page always writes a NEW row and activates it, so the list is the
 *  edit history and `is_active` marks the one production reads. */
export interface SearchPromptVersion extends SearchPrompt {
  is_active?: boolean;
  created_at?: string | null;
  last_used_at?: string | null;
}

export interface SavedSearchRunStart {
  search_id: string;
  title?: string | null;
  description_id: string;
  matrix_id: string;
  status: SavedSearchRunState;
  candidate_count: number;
  /**
   * The prior finished run whose results this response serves — the run
   * itself (same search_id: young enough to reuse outright) or the seed of
   * an incremental refresh (ids differ: first status polls already carry the
   * prior articles while the run searches what was ingested since).
   */
  restored_from?: { search_id: string; finished_at: string | null } | null;
}

export interface SavedSearchRunStatus {
  search_id: string;
  title?: string | null;
  description_id: string;
  matrix_id: string;
  status: SavedSearchRunState;
  relevance: number;
  freshness: number;
  created_at?: string | null;
  finished_at?: string | null;
  results: string[];
  articles: StoryElement[];
  judged_count: number;
  cached_judgment_count: number;
  live_judgment_count: number;
  cached_relevant_count: number;
  cached_not_relevant_count: number;
  live_relevant_count: number;
  live_not_relevant_count: number;
  candidate_count: number;
  error?: string | null;
  /**
   * A finished run that could not judge its whole candidate pool. The results
   * are real but short, so the pane surfaces this rather than letting a
   * partial list read as a complete one.
   */
  warning?: string | null;
  /** The model that judged this run. */
  judge_model?: string | null;
  /**
   * How far back this run's candidate retrieval looked, in hours. Reported by the server rather than remembered by the composer, so
   * the 24h/72h control can tell "these results are the wider run, shown
   * narrowed" from "this needs a wider search" even across a remount or a
   * bookmarked run URL.
   */
  window_hours?: number | null;
  timing_details?: SearchTimingDetails | null;
}

export interface SearchTimingSpan {
  key: string;
  label: string;
  category: TimingCategory;
  start_ms: number;
  end_ms: number;
  duration_ms: number;
  detail?: string;
  metadata?: Record<string, unknown>;
}

export interface SearchTimingDetails {
  version: 1 | 2;
  clock: 'server_monotonic';
  total_ms: number;
  spans: SearchTimingSpan[];
  stats: Record<string, unknown>;
  /** Fast search phase durations, measured without constructing waterfall spans. */
  phase_timings_ms?: Record<string, number>;
  /**
   * phase_timings_ms collapsed into the buckets the
   * reader's timing waterfall displays (preflight, setup, query_embed,
   * vector_search, candidate_selection, misc_backend, llm_wave, after).
   * Runs stored before 2026-07-30 carry a single `embedding` bucket instead
   * of query_embed/vector_search, and runs stored before 2026-08-29 carry no
   * `preflight` bucket: the work it measures ran before the run existed.
   */
  latency_buckets_ms?: Record<string, number>;
  /**
   * run-relative start offset + duration per phase. Unlike
   * the buckets, these are true positions, so they say when a phase ran and
   * not just how long it took.
   */
  phase_spans_ms?: Record<string, { start_ms: number; duration_ms: number }>;
  /**
   * the phases folded into the `misc_backend` bucket, in the
   * order they ran. The bucket is a sum rather than a span -- these are what it
   * is a sum of, so a slow misc_backend can be attributed to a phase.
   */
  misc_backend_breakdown?: { key: string; duration_ms: number; start_ms: number }[];
  /** The two judges this run actually used. Absent on runs stored before the
   *  pair became configurable, which all used the code-owned default. */
  judge_covers?: SearchJudgeCovers | null;
  /** `primary_id+hedge_id`. Present on every fast-search run; the fallback
   *  the timing panel names covers from when `judge_covers` is absent. */
  judge_model?: string | null;
}

export async function listSearchModels(): Promise<SearchModel[]> {
  const data = await apiJson<SearchModel[]>('/api/search-models');
  return Array.isArray(data) ? data : [];
}

export async function getSearchJudgeConfig(): Promise<SearchJudgeConfig> {
  return apiJson<SearchJudgeConfig>('/api/admin/fast-search-judge');
}

export async function updateSearchJudgeConfig(payload: {
  primary_model_id: string;
  hedge_model_id: string;
  authoritative: JudgeCover;
}): Promise<SearchJudgeConfig> {
  return apiJson<SearchJudgeConfig>('/api/admin/fast-search-judge', {
    method: 'PUT',
    json: payload,
  });
}

export async function getSearchPrompt(): Promise<SearchPrompt> {
  return apiJson<SearchPrompt>('/api/admin/fast-search-prompt');
}

export async function getActiveFeedFilterPrompt(): Promise<SearchPrompt> {
  return apiJson<SearchPrompt>('/api/admin/prompt-workbench/feed_filter/prompts/active');
}

export async function updateSearchPrompt(payload: Omit<SearchPrompt, 'id' | 'prompt_type'>): Promise<SearchPrompt> {
  return apiJson<SearchPrompt>('/api/admin/fast-search-prompt', {
    method: 'PUT',
    json: payload,
  });
}

export async function listSearchPromptVersions(): Promise<SearchPromptVersion[]> {
  const data = await apiJson<SearchPromptVersion[]>('/api/admin/fast-search-prompt/versions');
  return Array.isArray(data) ? data : [];
}

export async function activateSearchPromptVersion(id: string): Promise<SearchPrompt> {
  return apiJson<SearchPrompt>(
    `/api/admin/fast-search-prompt/versions/${encodeURIComponent(id)}/activate`,
    { method: 'POST' },
  );
}

export async function listSavedSearches(): Promise<SavedSearch[]> {
  const data = await apiJson<SavedSearch[]>('/api/saved-searches');
  return Array.isArray(data) ? data : [];
}

export async function listRecentSearches(): Promise<RecentSearch[]> {
  const data = await apiJson<RecentSearch[]>('/api/searches/recent');
  return Array.isArray(data) ? data : [];
}

export async function getSavedSearch(id: string): Promise<SavedSearch> {
  return apiJson<SavedSearch>(`/api/saved-searches/${encodeURIComponent(id)}`);
}

export async function createSavedSearch(
  payload: SavedSearchPayload,
): Promise<SavedSearch> {
  return apiJson<SavedSearch>('/api/saved-searches', {
    method: 'POST',
    json: payload,
  });
}

export async function updateSavedSearch(
  id: string,
  payload: Partial<SavedSearchPayload>,
): Promise<SavedSearch> {
  return apiJson<SavedSearch>(`/api/saved-searches/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json: payload,
  });
}

export async function deleteSavedSearch(id: string): Promise<void> {
  await apiJson<void>(`/api/saved-searches/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function runSearch(payload: SavedSearchRunPayload): Promise<SavedSearchRunStart> {
  return apiJson<SavedSearchRunStart>('/api/fast-searches/run', {
    method: 'POST',
    json: payload,
  });
}

export async function getSearchRunStatus(searchId: string): Promise<SavedSearchRunStatus> {
  return apiJson<SavedSearchRunStatus>(`/api/fast-search-runs/${encodeURIComponent(searchId)}`);
}

/**
 * One candidate a search run considered, with both of its provenance
 * axes. `retrieval_*` says why the candidate is in the pool; `verdict_origin`
 * and `donor_*` say why it carries the verdict it does. They are separate
 * because they cross: a candidate can be retrieved because a neighbour liked it
 * and then judged fresh, or retrieved by cosine and given a borrowed verdict.
 */
export interface SearchAuditCandidate {
  article_id: string;
  title: string | null;
  url: string | null;
  canonical_domain: string | null;
  retrieval_source: string | null;
  retrieval_rank: number | null;
  embedding_similarity: number | null;
  bm25_rank: number | null;
  base_quality_rank: number | null;
  selected: boolean;
  outcome: 'judged' | 'cut' | 'lost' | 'timed_out';
  promoted: boolean;
  promoted_donor_description_id: string | null;
  promoted_donor_search_id: string | null;
  promoted_donor_description: string | null;
  verdict_origin: 'fresh' | 'cache_exact' | 'cache_neighbor' | null;
  relevant: boolean | null;
  donor_description_id: string | null;
  donor_search_id: string | null;
  donor_description: string | null;
  donor_cosine: number | null;
  donor_relevant: boolean | null;
  shadow_judged: boolean;
  final_rank: number | null;
  shown: boolean;
  verdicts: SearchAuditVerdict[];
}

export interface SearchAuditVerdict {
  cover: number;
  relevant: boolean;
  relevance_score: number | null;
  justification: string | null;
  judge_model: string;
  batch_number: number;
  latency_ms: number | null;
}

export interface SearchAuditReuse {
  candidate_count?: number;
  exact_hits?: number;
  neighbor_hits?: number;
  cold?: number;
  promoted?: number;
  reuse_fraction?: number | null;
  neighbor_fraction?: number | null;
  donor_cosine_min?: number | null;
  donor_cosine_p50?: number | null;
  donor_cosine_max?: number | null;
  donors_available?: number;
  shadow_judged?: number;
  shadow_agreements?: number;
  neighbor_injected?: number;
}

export interface SearchAudit {
  run: {
    id: string;
    description_id: string;
    description: string | null;
    title: string | null;
    status: string | null;
    created_at: string | null;
    finished_at: string | null;
    judge_model: string | null;
    prompt_id: string | null;
    username: string | null;
    relevance: number | null;
    freshness: number | null;
    warning: string | null;
    total_ms: number | null;
  };
  header: {
    candidates_retrieved: number | null;
    candidates_selected: number | null;
    cache_hits: number | null;
    live_judged: number | null;
    results: number | null;
    cached_relevant: number | null;
    live_relevant: number | null;
    cover_disagreements: number | null;
    one_cover_only: number | null;
    both_covers_relevant: number | null;
    both_covers_not_relevant: number | null;
    lost: number | null;
    timed_out: number | null;
    hit_deadline: boolean | null;
    reuse: SearchAuditReuse;
    selected_by_retrieval_source: Record<string, number>;
    by_verdict_origin: Record<string, number>;
    by_outcome: Record<string, number>;
    shown_count: number;
    borrowed_shadow_judged: number;
    borrowed_shadow_agreements: number;
    borrowed_shadow_agreement_rate: number | null;
  };
  candidates: SearchAuditCandidate[];
}

export async function getSearchAudit(searchId: string): Promise<SearchAudit> {
  return apiJson<SearchAudit>(
    `/api/admin/fast-search-runs/${encodeURIComponent(searchId)}/audit`,
  );
}

/**
 * Resolve a saved search to its newest run, so an audit URL can be run-scoped
 * from then on. A saved-search-scoped audit link would follow the search's
 * *current* run, which need not contain the candidate or donor being examined.
 */
export async function getLatestSearchRun(
  descriptionId: string,
): Promise<{ id: string; created_at: string; status: string }> {
  return apiJson<{ id: string; created_at: string; status: string }>(
    `/api/admin/fast-searches/${encodeURIComponent(descriptionId)}/latest-run`,
  );
}

export interface TopSavedSearch {
  id: string;
  name: string;
  description: string;
  run_count: number;
}

export async function getTopSavedSearches(): Promise<TopSavedSearch[]> {
  const data = await apiJson<TopSavedSearch[]>('/api/saved-searches/top');
  return Array.isArray(data) ? data : [];
}

export interface SearchDescription {
  id: string;
  description: string;
}

export async function getSearchDescription(descriptionId: string): Promise<SearchDescription> {
  return apiJson<SearchDescription>(`/api/search-descriptions/${encodeURIComponent(descriptionId)}`);
}
